/* eslint-disable max-len */
import { Calendar } from "@react-admin/ra-calendar";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { IShowAppointmentDialogData } from "./CustomAppointmentList";
import { IShowAppointmentServiceDialogData } from "./AppointmentServiceList";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import {
  ListContextProvider,
  useList,
  // useNotify,
  useTranslate
} from "react-admin";
import { getZonedDate } from "../utils/helpers";
import { ArtistSpecifiedIcon } from "assets/Icons";
// do not remove these 3 import, it is required for the calendar to work
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import dayGridPlugin from "@fullcalendar/daygrid";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import timeGridPlugin from "@fullcalendar/timegrid";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import interactionPlugin from "@fullcalendar/interaction";
// do not remove these 3 import, it is required for the calendar to work

const eventColors = [
  "#C0E7F8",
  "#F8D1C0",
  "#E3C0F8",
  "#F8ECC0",
  "#BEF3E0",
  "#C0C6F8",
  "#F8DEC0",
  "#F8C0E5",
  "#CFF8C0",
  "#7EC1DE",
  "#EDB59D",
  "#BA7EDE",
  "#F0C899",
  "#7ED9B8",
  "#7E88DE",
  "#DE7EBD",
  "#AADF98"
];
const eventTextColors = [
  "#0D5675",
  "#752D0D",
  "#4D0D75",
  "#755E0D",
  "#0D754F",
  "#0D1775",
  "#75440D",
  "#750D52",
  "#266E0C",
  "#0A435C",
  "#5C230A",
  "#FFFFFF",
  "#5C360A",
  "#095237",
  "#FFFFFF",
  "#FFFFFF",
  "#1D5409"
];

interface ArtistSingleCalendarProps {
  locale: string;
  type: string;
  select: (arg: any, artistId: string) => void;
  editable?: boolean;
  isPopoverOpen?: boolean;
  handlePopoverOpen?: (note: string) => void;
  handlePopoverClose?: () => void;
  anchorEl?: HTMLElement | null;
  setShowDialogData?: Dispatch<
    React.SetStateAction<
      IShowAppointmentDialogData | IShowAppointmentServiceDialogData | null
    >
  >;
  selectable?: boolean;
  artistId: string;
  isPrev: boolean;
  isNext: boolean;
  setSelectedCalendarDate: Dispatch<
    SetStateAction<{
      title: string;
      dateForFilter: string;
    } | null>
  >;
  calendarsDate: Dayjs | null;
  index: number;
  appointmentServicesData: any[];
  setCalendarsDate: Dispatch<SetStateAction<dayjs.Dayjs | null>>;
  businessHours: any;
  refetch: any;
  timezone: string;
  refresh: () => void;
}

const ArtistSingleCalendar = ({
  locale,
  type,
  select,
  editable,
  isPopoverOpen,
  handlePopoverOpen,
  handlePopoverClose,
  anchorEl,
  setShowDialogData,
  selectable,
  isPrev,
  isNext,
  setSelectedCalendarDate,
  calendarsDate,
  index,
  appointmentServicesData,
  setCalendarsDate,
  businessHours,
  artistId,
  refetch,
  refresh,
  timezone
}: ArtistSingleCalendarProps) => {
  const translate = useTranslate();
  let calRef = useRef<any>();
  const { user } = User.useUser();
  const { api } = Axios.useAxios();
  // const notify = useNotify();
  const [isFirstRender, setIsFirstRender] = useState(true);

  // const offset = new Date().getTimezoneOffset();
  // const hour = offset <= -720 ? 0 : offset >= 720 ? 16 : 12;
  useEffect(() => {
    if (calRef.current) {
      setSelectedCalendarDate({
        title: calRef?.current?.currentDataManager?.data?.viewTitle,
        dateForFilter: JSON.parse(
          JSON.stringify(
            dayjs(
              isFirstRender
                ? dayjs()
                : calRef?.current?.currentDataManager?.data?.currentDate
            )
              .hour(12)
              .minute(0)
              .second(0)
              ?.format()
          )
        )
      });
      if (isFirstRender) {
        calRef?.current?.gotoDate(
          dayjs().hour(12).minute(0).second(0)?.format()
        );
        setIsFirstRender(false);
      }
      if (isPrev) {
        calRef?.current?.gotoDate(
          dayjs(calRef?.current?.currentDataManager?.data?.currentDate)
            .hour(12)
            .minute(0)
            .second(0)
            .subtract(1, "day")
            ?.format()
        );
        localStorage.setItem(
          "ServiceCalendarDateFilter",
          JSON.stringify(
            dayjs(calRef?.current?.currentDataManager?.data?.currentDate)
          )
        );
      }
      if (isNext) {
        calRef?.current?.gotoDate(
          dayjs(calRef?.current?.currentDataManager?.data?.currentDate)
            .hour(12)
            .minute(0)
            .second(0)
            .add(1, "day")
            ?.format()
        );
        localStorage.setItem(
          "ServiceCalendarDateFilter",
          JSON.stringify(
            dayjs(calRef?.current?.currentDataManager?.data?.currentDate)
          )
        );
      }
      if (calendarsDate) {
        calRef?.current?.gotoDate(
          calendarsDate.hour(12).minute(0).second(0)?.format()
        );
        setCalendarsDate(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calRef, isPrev, isNext, calendarsDate, setSelectedCalendarDate]);

  const listContext = useList({
    data: appointmentServicesData,
    isLoading: false
  });

  const converter = (event: any) => {
    const price = !!event?.add_ons?.length
      ? event?.service?.price +
        event.add_ons.reduce((acc: number, curr: any) => {
          acc += curr?.core_service?.price;
          return acc;
        }, 0)
      : event?.service?.price;
    const start = getZonedDate(
      event?.start_time,
      event?.appointment?.location?.timezone
    );
    const end = getZonedDate(
      event?.end_time,
      event?.appointment?.location?.timezone
    );
    const guestName =
      event?.person_number > 0
        ? `${event?.user?.first_name}'s friend (P${event?.person_number + 1})`
        : "";
    const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const retval = {
      id: String(event.id),
      title: `${event?.user?.first_name} ${event?.user?.last_name}`,
      start: start.toISOString(),
      end: end.toISOString(),
      extendedProps: {
        artistsIds: event?.artist?.user?.id,
        artistId: event?.artist?.id,
        artistFullName: `${event?.artist?.user?.first_name} ${event?.artist?.user?.last_name}`,
        artistPhoto:
          event?.artist?.cover_photo_url ||
          event?.artist?.user?.photo_url ||
          "monogram",
        clientPhoto: event?.user?.photo_url || "monogram",
        guestName,
        appointmentId: event?.appointment?.id,
        appointmentNotes: event?.appointment?.notes,
        duration: durationInMinutes,
        serviceDuration: event?.service?.duration,
        servicePrice: price,
        serviceName: event?.service?.name,
        locationName:
          event?.appointment?.location?.nickname ||
          event?.appointment?.location?.name,
        addOns: event?.add_ons || [],
        time: `${start.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })} - ${end.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })}`,
        startDateAndTime: event?.start_time,
        locationTimezone: event?.appointment?.location?.timezone,
        isBlockedTime: event?.is_blocked_time,
        clientPhoneNumber: event?.user?.phone_number,
        is_artist_specified: event?.appointment?.is_artist_specified
      },
      backgroundColor: event?.is_blocked_time
        ? "#dbdbdb"
        : eventColors[index] || "#C0C6F8",
      borderColor: event?.is_blocked_time
        ? "#dbdbdb"
        : eventColors[index] || "#C0C6F8",
      textColor: event?.is_blocked_time
        ? "#000000"
        : eventTextColors[index] || "#0D1775",
      editable: true,
      durationEditable: false,
      // droppable: true,
      // overlap: true,
      interactive: true,
      display: "block",
      duration: durationInMinutes,
      start_time: start,
      end_time: end
    };
    return retval;
  };

  const editWithEventChange = async (eventChangeInfo: any) => {
    const eventData = eventChangeInfo.event;
    const eventStart = eventData?.start;
    const eventEnd = eventData?.end;
    const appointmentServiceId = eventData?.id;
    const appointmentId = eventData.extendedProps.appointmentId;

    const startTime = dayjs(eventStart).tz(timezone, true).format();

    const endTime = dayjs(eventEnd).tz(timezone, true).format();

    await api.post("/v1/appointments/move-one-appointment-service", {
      appointmentId,
      appointmentServiceId,
      startTime,
      endTime
    });
    refetch();
  };

  const editWithEventReceive = async (
    eventReceiveInfo: any,
    editedArtistId: string
  ) => {
    const eventData = eventReceiveInfo.event;
    const eventStart = eventData?.start;
    const eventEnd = eventData?.end;
    const appointmentServiceId = eventData?.id;
    const appointmentId = eventData.extendedProps.appointmentId;

    const startTime = dayjs(eventStart).tz(timezone, true).format();
    const endTime = dayjs(eventEnd).tz(timezone, true).format();

    await api.post("/v1/appointments/appointment-service-edit", {
      appointmentServiceId,
      artistId: editedArtistId
    });
    await api.post("/v1/appointments/move-one-appointment-service", {
      appointmentId,
      appointmentServiceId,
      startTime,
      endTime
    });
    refetch();
    refresh();
  };

  return (
    <div>
      <ListContextProvider value={listContext}>
        <Calendar
          // selectOverlap={false}
          // eventOverlap={false}
          // slotEventOverlap={false}
          droppable={
            artistId?.startsWith("non-existing-artist-") ? false : true
          }
          dragScroll={true}
          allDaySlot={false}
          locale={locale}
          convertToEvent={converter}
          // plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          datesSet={(dateInfo) => {
            if (!calRef?.current) {
              calRef.current = dateInfo?.view?.calendar;
            }
          }}
          select={(arg) => select(arg, artistId)}
          selectable={selectable}
          eventChange={(eventChangeInfo) => {
            editWithEventChange(eventChangeInfo);
          }}
          eventReceive={(eventChangeInfo) => {
            editWithEventReceive(eventChangeInfo, artistId);
          }}
          // eventDragStart={(e) => {
          //   console.log("eventDragStart:", e);
          // }}
          // eventDragStop={(e) => {
          //   console.log("e:", e);
          // }}
          // _noEventDrop={() => {
          //   console.log("_noEventDrop:");
          // }}
          // eventResize={(eventChangeInfo) => {
          //   editWithEventChange(eventChangeInfo);
          // }}
          // eventDrop={(eventChangeInfo) => {
          //   editWithEventReceive(eventChangeInfo, artistId);
          // }}
          drop={(e) => {
            e?.view?.calendar?.removeAllEventSources();
          }}
          // eventAllow={(e) => {
          //   console.log("eventAllow:", e);
          //   return true;
          // }}
          // eventMouseLeave={(e) => {
          //   if (e) {
          //     console.log("eventMouseLeave:", e);
          //     // e.view.calendar.getEventById(e.event.id)?.remove();
          //     // e?.event?.source?.remove();
          //     // e?.event?.remove();
          //   }
          // }}
          // dragRevertDuration={0}
          eventInteractive={true}
          // eventLeave={(e) => {
          //   if (e) {
          //     console.log("e:", e);
          //   }
          // }}
          eventClassNames={(args) => {
            return args?.backgroundColor ? "" : "event-hidden";
          }}
          eventContent={(e: any): JSX.Element => {
            if (type === "appointmentService") {
              return (
                <>
                  {e?.event?._def?.extendedProps?.appointmentNotes ? (
                    <>
                      <div
                        id="customEventWrapper"
                        title={
                          e?.event?._def?.extendedProps?.isBlockedTime
                            ? `${translate("appointment.blocked_time")} - ${
                                e?.event?._def?.extendedProps?.time
                              }`
                            : e?.event?._def?.extendedProps?.serviceName +
                              (!!e?.event?._def?.extendedProps?.addOns?.length
                                ? ` (${e?.event?._def?.extendedProps?.addOns
                                    ?.map((item: any) => item.core_service.name)
                                    ?.join(", ")})`
                                : "") +
                              ` - ${
                                e?.event?._def?.extendedProps?.guestName
                                  ? e?.event?._def?.extendedProps?.guestName
                                  : e?.event?._def?.title
                              }` +
                              " - " +
                              e?.event?._def?.extendedProps?.time
                        }
                      >
                        <p
                          id="customEventTime"
                          className={
                            e?.event?._def?.extendedProps?.duration > 30
                              ? "line-clamp-2"
                              : "line-clamp-1"
                          }
                        >
                          {e?.event?._def?.extendedProps?.isBlockedTime
                            ? ""
                            : e?.event?._def?.extendedProps?.serviceName}
                          {!!e?.event?._def?.extendedProps?.addOns?.length
                            ? ` (${e?.event?._def?.extendedProps?.addOns
                                ?.map((item: any) => item.core_service.name)
                                ?.join(", ")})`
                            : ""}
                        </p>
                        <p id="customEventTitle">
                          {e?.event?._def?.extendedProps?.isBlockedTime
                            ? translate("appointment.blocked_time")
                            : e?.event?._def?.extendedProps?.guestName
                            ? e?.event?._def?.extendedProps?.guestName
                            : e?.event?._def?.title}
                        </p>
                        <p id="customEventTime">
                          {e?.timeText || e?.event?._def?.extendedProps?.time}
                        </p>
                      </div>
                      <div
                        id="customEventNote"
                        style={{
                          backgroundColor: eventTextColors[index] || "#C0C6F8"
                        }}
                        aria-owns={
                          isPopoverOpen ? "mouse-over-popover" : undefined
                        }
                        aria-haspopup="true"
                        onMouseEnter={() =>
                          handlePopoverOpen &&
                          handlePopoverOpen(
                            e?.event?._def?.extendedProps?.appointmentNotes
                          )
                        }
                        onMouseLeave={handlePopoverClose}
                      />
                    </>
                  ) : (
                    <div
                      id="customEventWrapper"
                      title={
                        e?.event?._def?.extendedProps?.isBlockedTime
                          ? `${translate("appointment.blocked_time")} - ${
                              e?.event?._def?.extendedProps?.time
                            }`
                          : e?.event?._def?.extendedProps?.serviceName +
                            (!!e?.event?._def?.extendedProps?.addOns?.length
                              ? ` (${e?.event?._def?.extendedProps?.addOns
                                  ?.map((item: any) => item.core_service.name)
                                  ?.join(", ")})`
                              : "") +
                            ` - ${
                              e?.event?._def?.extendedProps?.guestName
                                ? e?.event?._def?.extendedProps?.guestName
                                : e?.event?._def?.title
                            }` +
                            " - " +
                            e?.event?._def?.extendedProps?.time
                      }
                    >
                      <p
                        id="customEventTime"
                        className={
                          e?.event?._def?.extendedProps?.duration > 30
                            ? "line-clamp-2"
                            : "line-clamp-1"
                        }
                      >
                        {e?.event?._def?.extendedProps?.isBlockedTime
                          ? ""
                          : e?.event?._def?.extendedProps?.serviceName}
                        {!!e?.event?._def?.extendedProps?.addOns?.length
                          ? ` (${e?.event?._def?.extendedProps?.addOns
                              ?.map((item: any) => item.core_service.name)
                              ?.join(", ")})`
                          : ""}
                      </p>
                      <p id="customEventTitle">
                        {e?.event?._def?.extendedProps?.isBlockedTime
                          ? translate("appointment.blocked_time")
                          : e?.event?._def?.extendedProps?.guestName
                          ? e?.event?._def?.extendedProps?.guestName
                          : e?.event?._def?.title}
                      </p>
                      <p id="customEventTime">
                        {e?.timeText || e?.event?._def?.extendedProps?.time}
                      </p>
                    </div>
                  )}
                  {e?.event?._def?.extendedProps?.is_artist_specified && (
                    <div id="artistSpecifiedStarIcon">
                      <ArtistSpecifiedIcon />
                    </div>
                  )}
                </>
              );
            }
            return <div />;
          }}
          editable={editable}
          mutationMode="optimistic"
          height="3000px"
          headerToolbar={{
            left: "",
            center: "",
            right: "timeGridDay"
          }}
          initialView="timeGridDay"
          nowIndicator={true}
          businessHours={businessHours}
          eventClick={(e) => {
            if (type === "appointmentService" && setShowDialogData) {
              if (anchorEl) return;
              setShowDialogData({
                id: e?.event?._def?.publicId,
                client: e?.event?._def?.title,
                time: e?.event?._def?.extendedProps?.time,
                artistId: e?.event?._def?.extendedProps?.artistId,
                artist: e?.event?._def?.extendedProps?.artistFullName,
                serviceName: e?.event?._def?.extendedProps?.serviceName,
                date: dayjs(e?.event?._def?.extendedProps?.start_time).format(
                  "D MMMM YYYY (dddd)"
                ),
                artistPhoto: e?.event?._def?.extendedProps.artistPhoto,
                clientPhoto: e?.event?._def?.extendedProps.clientPhoto,
                guestName: e?.event?._def?.extendedProps.guestName,
                appointmentId: e?.event?._def?.extendedProps.appointmentId,
                appointmentNotes:
                  e?.event?._def?.extendedProps.appointmentNotes,
                serviceDuration: e?.event?._def?.extendedProps?.duration,
                servicePrice: e?.event?._def?.extendedProps?.servicePrice,
                locationName: e?.event?._def?.extendedProps?.locationName,
                addOns: e?.event?._def?.extendedProps?.addOns,
                startDateAndTime:
                  e?.event?._def?.extendedProps?.startDateAndTime,
                locationTimezone:
                  e?.event?._def?.extendedProps?.locationTimezone,
                isBlockedTime: e?.event?._def?.extendedProps?.isBlockedTime,
                clientPhoneNumber:
                  e?.event?._def?.extendedProps?.clientPhoneNumber
              });
            }
          }}
          slotDuration="00:15:00"
          slotLabelInterval="01:00"
          snapDuration="00:05:00"
          sx={{
            "& .fc-toolbar.fc-header-toolbar": {
              marginBottom: "55px",
              maxHeight: user.impersonate_company_id ? "94px" : "54px",
              minHeight: user.impersonate_company_id ? "94px" : "54px",
              display: "none"
            },
            "& .fc-daygrid-body-natural": {
              display: "none" // remove all day section row
            },
            "& .fc-timegrid-divider": {
              display: "none" // remove short empty space below all day section row
            },
            "& .fc-toolbar-title": {
              color: "rgba(48, 47, 46, 0.6)",
              fontSize: "18px",
              lineHeight: "31px",
              verticalAlign: "middle",
              fontWeight: "500",
              fontFamily: "Inter",
              textTransform: "uppercase",
              display: "inline-block"
            },
            ".fc-scrollgrid": {
              border: "none"
            },
            "& .fc-timegrid-slot-label-cushion": {
              color: "rgba(48, 47, 46, 0.6)",
              fontSize: "12px",
              fontWeight: "400",
              fontFamily: "Inter",
              textTransform: "uppercase"
            },
            "& .fc .fc-timegrid-slot-label": {
              display: "none"
            },
            "& .fc-daygrid-day-number": {
              color: "rgba(48, 47, 46, 0.6)",
              fontSize: "18px",
              fontWeight: "400",
              fontFamily: "Inter"
            },
            "& .fc-col-header-cell-cushion": {
              color: "rgba(48, 47, 46, 0.6)",
              fontSize: "12px",
              fontWeight: "400",
              fontFamily: "Inter",
              textTransform: "uppercase",
              display: "none"
            },
            "& .fc-direction-ltr .fc-timegrid-slot-label-frame": {
              textAlign: "center"
            },
            "& .fc-button": {
              display: "none",
              backgroundColor: "transparent",
              color: "rgba(48, 47, 46, 0.95)",
              borderColor: "#ddd !important",
              fontSize: "13px",
              fontWeight: 400,
              textTransform: "capitalize",
              borderRadius: "24px",
              lineHeight: "18px",
              fontFamily: "Inter"
            },
            "& .fc-button-primary:disabled": {
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.07)",
                color: "rgba(0, 0, 0, 0.54)"
              }
            },
            "& .fc-button:hover": {
              borderColor: "#ddd !important",
              backgroundColor: "rgba(184, 114, 42, 0.07)",
              color: "rgba(48, 47, 46, 0.95)"
            },
            "& .fc-button-primary:not(:disabled):active": {
              backgroundColor: "rgba(184, 114, 42, 0.1)",
              color: "rgba(48, 47, 46, 0.95)"
            },
            "& .fc-button-primary:not(:disabled).fc-button-active": {
              backgroundColor: "#B8722A",
              "&:hover": {
                backgroundColor: "#B8722A"
              },
              color: "#ffffff",
              borderColor: "#B8722A !important"
            },
            "& .fc-today-button": {
              marginLeft: "0px !important"
            },
            "& .fc-scrollgrid-liquid": {
              borderRadius: "12px"
            },
            "& .fc-theme-standard th": {
              borderTopRightRadius: "12px"
            },
            "& .fc-scroller": {
              overflow: "hidden !important"
            },
            "& .fc-day-today": {
              backgroundColor: "transparent !important",
              borderRadius: "0px !important"
            },
            "& .fc-prev-button": {
              position: "absolute",
              top: "109px",
              left: "0px",
              padding: "5px 10px 5px 11px",
              zIndex: 10,
              borderColor: "#ddd !important",
              borderBottom: "none",
              borderRight: "none",
              borderRadius: 0,
              borderTopLeftRadius: "12px",
              backgroundColor: "transparent",
              color: "rgba(48, 47, 46, 0.38)"
            },
            "& .fc-next-button": {
              position: "absolute",
              top: "109px",
              right: "0px",
              padding: "5px 10px 5px 11px",
              zIndex: 10,
              borderColor: "#ddd !important",
              borderBottom: "none",
              borderLeft: "none",
              borderRadius: 0,
              borderTopRightRadius: "12px",
              backgroundColor: "transparent",
              color: "rgba(48, 47, 46, 0.38)"
            },
            "& .fc-timegrid-event-harness-inset .fc-timegrid-event, .fc-timegrid-event.fc-event-mirror, .fc-timegrid-more-link":
              {
                boxShadow: "0px 0px 0px 1px #F6F4F1",
                border: "none",
                borderRadius: "8px",
                padding: "4px"
              },
            "& .fc-theme-standard .fc-popover": {
              backgroundColor: "#ffffff !important"
            },
            "& #customEventWrapper": {
              position: "sticky",
              top: "0px",
              bottom: "0px",
              maxHeight: "100%",
              overflow: "hidden"
            },
            "& #customEventTitle": {
              fontSize: "17px",
              fontWeight: 600,
              fontFamily: "Inter",
              lineHeight: "18px"
            },
            "& #customEventGroupBooking": {
              fontSize: "17px",
              fontWeight: 600,
              fontFamily: "Inter",
              lineHeight: "18px",
              textTransform: "uppercase"
            },
            "& #customEventTime": {
              fontSize: "13px",
              fontWeight: 500,
              fontFamily: "Inter",
              lineHeight: "14px"
            },
            "& #customEventNote": {
              height: "10px",
              width: "10px",
              position: "absolute",
              top: "-6px",
              left: "-5px",
              zIndex: "inherit"
            },
            "& #artistSpecifiedStarIcon": {
              position: "absolute",
              top: "5px",
              right: "5px",
              zIndex: "inherit"
            },
            "& .fc-daygrid-more-link": {
              fontFamily: "Inter"
            },
            "& .fc-highlight": {},
            "& .event-hidden": {
              display: "none"
            },
            "& .fc .fc-timegrid-now-indicator-line": {
              borderColor: "#F27742"
            }
          }}
        />
      </ListContextProvider>
    </div>
  );
};

export default ArtistSingleCalendar;
